'use client';

import { c as _c } from "react/compiler-runtime";
import type { CheckedPositionAccountingResult } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { buildShareUrl } from '@/components/portfolio/share/share-pnl-button.utils';
import { onEvent } from '@/events';
import { useUserNetwork } from '@/lib/network/client';
import { openShareModal } from '@/lib/share';
import { computeRealizedWin, isViralWin } from '@/lib/trading/viral-win';
import { fireResolutionFireworks, fireTradeBurst } from '@/lib/ui/confetti';
import { useTxSubmissionStore } from '@/stores/tx-submission';
import { useViralShareStore } from '@/stores/viral-share';
import type { ConfettiOrigin } from './trade-executor-provider';
const CLAIM_INSTRUCTIONS: ReadonlySet<string> = new Set(['claim_prize', 'claim_backer_fees']);

/** Snapshot captured at sell-submit so the confirmed-stage effect can decide
 *  whether to fire the viral celebration (MOR-540). All amounts are D6 bigints. */
interface PendingViralSell {
  proceedsD6: bigint;
  accounting: CheckedPositionAccountingResult;
  marketPubkey: string;
  walletPubkey: string;
  outcomeIndex: number;
  marketTitle: string;
  outcomeLabel: string;
}

/**
 * Celebratory confetti — fired the instant a trade reaches the `confirmed`
 * stage (the SDK sets it when the indexer's NATS trade event lands, before the
 * slow Turnkey signature poll), so the burst aligns with the success toast.
 * The provider is mounted ONCE per page, so a single confirm fires exactly one
 * burst — no per-form-instance visible-origin gate is needed. A pre-existing
 * confirmed entry at mount is treated as already celebrated (seeding guard).
 *
 * Owns (and returns) the two submit→confirm carrier refs: the confetti origin
 * anchored over the requesting surface, and the sell snapshot for the
 * viral-win check. The provider arms them at submit; both are consumed
 * (nulled) on the confirm they decorate.
 */
export function useTradeCelebration() {
  const $ = _c(6);
  const pendingConfettiOriginRef = useRef(null);
  const pendingViralSellRef = useRef(null);
  const t = useTranslations("trade");
  const {
    network
  } = useUserNetwork();
  const txSubmissions = useTxSubmissionStore(_temp);
  const celebratedTradesRef = useRef(null);
  const confettiSeededRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== network || $[1] !== t || $[2] !== txSubmissions) {
    t0 = () => {
      const celebrated = celebratedTradesRef.current ?? new Set();
      celebratedTradesRef.current = celebrated;
      const seeding = !confettiSeededRef.current;
      confettiSeededRef.current = true;
      for (const entry of Object.values(txSubmissions)) {
        if (entry.stage !== "confirmed" || celebrated.has(entry.traceId)) {
          continue;
        }
        if (CLAIM_INSTRUCTIONS.has(entry.instructionName)) {
          continue;
        }
        if (seeding) {
          celebrated.add(entry.traceId);
          continue;
        }
        celebrated.add(entry.traceId);
        const origin = pendingConfettiOriginRef.current ?? undefined;
        pendingConfettiOriginRef.current = null;
        const pending = pendingViralSellRef.current;
        pendingViralSellRef.current = null;
        const win = pending !== null ? computeRealizedWin({
          proceedsD6: pending.proceedsD6,
          accounting: pending.accounting
        }) : null;
        const viralStore = useViralShareStore.getState();
        if (pending !== null && isViralWin(win) && !viralStore.hasCelebrated(pending.marketPubkey)) {
          const url = buildShareUrl(pending.marketPubkey, pending.walletPubkey, pending.outcomeIndex, network === "mainnet" ? "mainnet" : "devnet");
          if (url.length > 0) {
            viralStore.markCelebrated(pending.marketPubkey);
            fireResolutionFireworks();
            openShareModal({
              surface: {
                kind: "pnl_position",
                marketPubkey: pending.marketPubkey,
                walletPubkey: pending.walletPubkey,
                outcomeIndex: pending.outcomeIndex
              },
              url,
              text: t("viralShare.tweetText", {
                multiple: win.label,
                market: pending.marketTitle
              }),
              title: t("viralShare.modalTitle", {
                multiple: win.label
              })
            });
            onEvent("viral_pnl_auto_prompted", {
              marketPubkey: pending.marketPubkey,
              walletPubkey: pending.walletPubkey,
              outcomeIndex: pending.outcomeIndex
            });
            continue;
          }
        }
        fireTradeBurst(origin);
      }
    };
    t1 = [txSubmissions, network, t];
    $[0] = network;
    $[1] = t;
    $[2] = txSubmissions;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      pendingConfettiOriginRef,
      pendingViralSellRef
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp(s) {
  return s.submissions;
}